/**
 * Durable operation key for Create Invoice (SPEC-christa-contacts-identity-2026-10-05 R4 AC6).
 *
 * Pressing Create Invoice once must make exactly one invoice, even if the answer is lost, the request times out,
 * she presses again, reloads, or opens a second tab. So before anything is sent this module SAVES the pending
 * intent and its key; every retry, reload or second tab sends the SAME saved request with the SAME key; and the
 * server (which applies a key at most once and answers a repeat with the original invoice) is the only authority
 * on whether it happened. This module never decides that; it only remembers what was sent.
 *
 * Records live in localStorage (shared by tabs, survives a reload), keyed by tenant AND project. Only the request
 * and the key are stored: never an auth token or header. Every storage call is wrapped; if storage is blocked the
 * runner still sends (same-tab retry keeps the key in memory) and she can still create.
 *
 * Same shape as `invoice-merge-operation.ts`; free of react and api imports (the sender is injected) so it is
 * unit-testable alone.
 */
import { newOperationKey } from './invoice-merge-operation';

const KEY_PREFIX = 'invoice-create-operation:v1:';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface CreateLineRequest {
  billable_item_id: string;
  description: string;
  amount: number;
  tax_rate: number;
}

/** What the page sends, WITHOUT the key (the runner adds it). */
export type CreateInvoiceRequest = {
  project_id: string;
  lines?: CreateLineRequest[];
  [field: string]: unknown;
};

export interface PendingCreate {
  operationKey: string;
  request: CreateInvoiceRequest;
  createdAt: number;
}

/** The slice of the Storage interface this module needs (localStorage, or an in-memory stand-in). */
export interface CreateOperationStorage {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CreateResponseLike {
  invoice: { id: string };
  replayed?: boolean;
  [field: string]: unknown;
}

export type CreateSender = (request: CreateInvoiceRequest & { operation_key: string }) => Promise<CreateResponseLike>;

export type CreateRunResult =
  /** The invoice exists. `resumedEarlier` is true when it was an EARLIER attempt that was resolved, not this form. */
  | { kind: 'created'; response: CreateResponseLike; resumedEarlier: boolean }
  /** The server's answer never arrived (no response, 408, 429, 502, 503, 504). The key is kept; trying again is safe. */
  | { kind: 'unknown'; error: unknown; pending?: PendingCreate }
  /** Our API answered with a refusal (4xx), or with a 500 twice in a row (the same key was replayed once). The key is cleared and nothing was created. */
  | { kind: 'refused'; error: unknown }
  /** Nothing was pending. */
  | { kind: 'none' };

const memory = new Map<string, string>();
const memoryStorage: CreateOperationStorage = {
  get length() {
    return memory.size;
  },
  key: (i) => Array.from(memory.keys())[i] ?? null,
  getItem: (k) => memory.get(k) ?? null,
  setItem: (k, v) => void memory.set(k, v),
  removeItem: (k) => void memory.delete(k),
};

function defaultStorage(): CreateOperationStorage {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const probe = `${KEY_PREFIX}probe`;
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return window.localStorage;
    }
  } catch {
    // Blocked (private browsing, policy, quota): fall back to memory. Same-tab retry still works.
  }
  return memoryStorage;
}

/** Run a storage call; a blocked or throwing storage never reaches the caller. */
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function recordKey(tenantId: string, projectId: string): string {
  return `${KEY_PREFIX}${tenantId}:${projectId}`;
}

function parse(raw: string | null, now: number): PendingCreate | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingCreate;
    if (
      !parsed ||
      typeof parsed.operationKey !== 'string' ||
      !parsed.request ||
      typeof parsed.request.project_id !== 'string' ||
      typeof parsed.createdAt !== 'number' ||
      now - parsed.createdAt > MAX_AGE_MS
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Records that could not be written (blocked storage) are kept here so a retry in the same tab still reuses the
 * key. Cleared with the real record.
 */
const unsaved = new Map<string, PendingCreate>();

export function loadPendingCreate(
  tenantId: string,
  projectId: string,
  storage: CreateOperationStorage = defaultStorage(),
  now: number = Date.now(),
): PendingCreate | null {
  const key = recordKey(tenantId, projectId);
  const raw = safe(() => storage.getItem(key), null);
  const found = parse(raw, now);
  if (raw && !found) safe(() => storage.removeItem(key), undefined);
  if (found) return found;
  const held = unsaved.get(key);
  if (held && now - held.createdAt <= MAX_AGE_MS) return held;
  unsaved.delete(key);
  return null;
}

/** Every pending create for this tenant (any project). A reload loses the project, so the page looks here. */
export function listPendingCreates(
  tenantId: string,
  storage: CreateOperationStorage = defaultStorage(),
  now: number = Date.now(),
): PendingCreate[] {
  const prefix = `${KEY_PREFIX}${tenantId}:`;
  const names: string[] = [];
  safe(() => {
    for (let i = 0; i < storage.length; i += 1) {
      const name = storage.key(i);
      if (name && name.startsWith(prefix)) names.push(name);
    }
  }, undefined);
  const out: PendingCreate[] = [];
  for (const name of names) {
    const raw = safe(() => storage.getItem(name), null);
    const found = parse(raw, now);
    if (found) out.push(found);
    else safe(() => storage.removeItem(name), undefined);
  }
  for (const [name, held] of unsaved) {
    if (name.startsWith(prefix) && !out.some((p) => p.operationKey === held.operationKey) && now - held.createdAt <= MAX_AGE_MS) {
      out.push(held);
    }
  }
  return out.sort((a, b) => b.createdAt - a.createdAt);
}

function savePendingCreate(
  tenantId: string,
  record: PendingCreate,
  storage: CreateOperationStorage,
): void {
  const key = recordKey(tenantId, record.request.project_id);
  const wrote = safe(() => {
    storage.setItem(key, JSON.stringify(record));
    return true;
  }, false);
  if (!wrote) unsaved.set(key, record);
}

export function clearPendingCreate(
  tenantId: string,
  projectId: string,
  storage: CreateOperationStorage = defaultStorage(),
): void {
  const key = recordKey(tenantId, projectId);
  unsaved.delete(key);
  safe(() => storage.removeItem(key), undefined);
}

// R5 (carry-over from R4's christa-reviewer): after "It is not there, start over" the next create on that project may hit
// "number already exists" because the first press DID make the invoice. This remembers (in memory, per project, for one
// create) that she just started over, so the refusal can say "it may be the one you just made". Never stored.
const startedOver = new Map<string, number>();
const STARTED_OVER_MS = 30 * 60 * 1000;

export function markStartedOver(projectId: string, now: number = Date.now()): void {
  startedOver.set(projectId, now);
}

/** True once for a project she started over on within the last 30 minutes; forgets it as it answers. */
export function takeStartedOver(projectId: string | undefined, now: number = Date.now()): boolean {
  if (!projectId) return false;
  const at = startedOver.get(projectId);
  startedOver.delete(projectId);
  return at !== undefined && now - at <= STARTED_OVER_MS;
}

/**
 * Forget every pending create, for every tenant and project. Called on logout so the next login (another person,
 * or the same one starting fresh) never inherits an unresolved record.
 */
export function clearAllPendingCreates(storage: CreateOperationStorage = defaultStorage()): void {
  const names: string[] = [];
  safe(() => {
    for (let i = 0; i < storage.length; i += 1) {
      const name = storage.key(i);
      if (name && name.startsWith(KEY_PREFIX)) names.push(name);
    }
  }, undefined);
  for (const name of names) safe(() => storage.removeItem(name), undefined);
  unsaved.clear();
}

/**
 * Two requests are the SAME intent when everything the server compares is equal. The operation key is excluded
 * (it is what we are deciding whether to reuse); object key order does not matter, line order does.
 */
export function sameCreateIntent(a: CreateInvoiceRequest, b: CreateInvoiceRequest): boolean {
  const norm = (r: CreateInvoiceRequest) => {
    const { operation_key: _ignored, ...rest } = r as CreateInvoiceRequest & { operation_key?: string };
    return JSON.stringify(sortKeys(rest));
  };
  return norm(a) === norm(b);
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([x], [y]) => x.localeCompare(y))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return value;
}

/**
 * True only when the answer genuinely never arrived, so the invoice may or may not exist: no response at all
 * (network error, timeout), 408, 429, or a proxy/gateway failure (502, 503, 504).
 *
 * Every OTHER status is an answer from our own API. A 4xx means nothing was saved. A 500 usually means nothing was
 * saved, but can follow a commit, so the runner replays it ONCE with the same key (`isServerError`). A second 500
 * keeps the key and leaves the page in "Checking" with its exits (terminal cycle 2 MED A): clearing it there let a
 * re-press mint a new key and make a duplicate. The 2026-09-25 "the button won't let me create" trap is closed by
 * those exits ("Open the Invoices list", "It is not there, start over"), not by forgetting the key.
 */
export function isOutcomeUnknown(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  if (typeof status !== 'number') return true;
  return status === 408 || status === 429 || status === 502 || status === 503 || status === 504;
}

/**
 * True for a server error (HTTP 500 and the other 5xx that are not a gateway failure). The create commits in one
 * transaction, but the server can still answer 500 AFTER that commit (a later step, or building the answer, failed).
 * So a 500 is not proof that nothing was saved; it is replayed once with the same key to find out.
 */
export function isServerError(error: unknown): boolean {
  const status = (error as { status?: number } | null)?.status;
  return typeof status === 'number' && status >= 500 && !isOutcomeUnknown(error);
}

async function attempt(
  tenantId: string,
  record: PendingCreate,
  send: CreateSender,
  storage: CreateOperationStorage,
  resumedEarlier: boolean,
  replayedAfterServerError = false,
): Promise<CreateRunResult> {
  try {
    const response = await send({ ...record.request, operation_key: record.operationKey });
    clearPendingCreate(tenantId, record.request.project_id, storage);
    return { kind: 'created', response, resumedEarlier };
  } catch (error) {
    if (isOutcomeUnknown(error)) return { kind: 'unknown', error, pending: record };
    if (isServerError(error) && !replayedAfterServerError) {
      // M5: ask the server once more with the SAME key. If the first try committed, this returns that invoice (shown as
      // created, never duplicated). If it did not, the server tries again or answers a refusal, and we treat it as not created.
      // The record stays saved until this second answer, so a lost answer here is still resolved by the same key.
      return attempt(tenantId, record, send, storage, resumedEarlier, true);
    }
    if (isServerError(error)) {
      // Terminal cycle 2 MED A: a second 500 on the same key is NOT proof that nothing was saved. A failure after the
      // commit that is deterministic (building the answer, reading the stored result) repeats on the replay. So the key
      // is KEPT and the page stays in "Checking" with its two exits; a re-press reuses this key instead of minting a
      // new one (which would make a duplicate).
      return { kind: 'unknown', error, pending: record };
    }
    clearPendingCreate(tenantId, record.request.project_id, storage);
    return { kind: 'refused', error };
  }
}

/**
 * Replay ONE record the caller already holds (same key, same request). A tab that lost the answer keeps its own
 * record in memory, so it can still check after another tab resolved the intent and cleared the shared storage.
 */
export async function replayPendingCreate(options: {
  tenantId: string;
  record: PendingCreate;
  send: CreateSender;
  storage?: CreateOperationStorage;
}): Promise<CreateRunResult> {
  return attempt(options.tenantId, options.record, options.send, options.storage ?? defaultStorage(), true);
}

/**
 * Create the invoice for this request, exactly once.
 *
 *  1. If an earlier attempt for this project is still unresolved and this is a DIFFERENT request, that earlier one
 *     is resolved first (replayed unchanged with its own key). If it created the invoice, we report that and send
 *     nothing new. If it is still unknown, we stop: the new content is never sent while the old outcome is open.
 *     Only a definite refusal clears it and lets the new request go out.
 *  2. Otherwise the key is reused when the intent is the same, or minted fresh for a deliberately new invoice.
 *  3. The record is saved BEFORE sending.
 */
export async function runCreateInvoice(options: {
  tenantId: string;
  request: CreateInvoiceRequest;
  send: CreateSender;
  storage?: CreateOperationStorage;
  now?: number;
}): Promise<CreateRunResult> {
  const { tenantId, request, send } = options;
  const storage = options.storage ?? defaultStorage();
  const now = options.now ?? Date.now();

  const pending = loadPendingCreate(tenantId, request.project_id, storage, now);
  if (pending && !sameCreateIntent(pending.request, request)) {
    const earlier = await attempt(tenantId, pending, send, storage, true);
    if (earlier.kind === 'created' || earlier.kind === 'unknown') return earlier;
    // refused: the earlier one is gone and created nothing; fall through to the new request.
  }

  const record: PendingCreate =
    pending && sameCreateIntent(pending.request, request) ? pending : { operationKey: newOperationKey(), request, createdAt: now };
  savePendingCreate(tenantId, record, storage);
  return attempt(tenantId, record, send, storage, false);
}

/**
 * Resolve what is pending for this tenant (or one project), newest first: the reload and second-tab entry. The first one that
 * created an invoice wins. If any is still unknown, that is reported (the others stay pending).
 */
export async function resumePendingCreates(options: {
  tenantId: string;
  /** When set, only this project's pending create is resumed; another project's never is. */
  projectId?: string;
  send: CreateSender;
  storage?: CreateOperationStorage;
  now?: number;
}): Promise<CreateRunResult> {
  const storage = options.storage ?? defaultStorage();
  const pending = listPendingCreates(options.tenantId, storage, options.now).filter(
    (p) => options.projectId === undefined || p.request.project_id === options.projectId,
  );
  if (pending.length === 0) return { kind: 'none' };
  let last: CreateRunResult = { kind: 'none' };
  for (const record of pending) {
    const result = await attempt(options.tenantId, record, options.send, storage, true);
    if (result.kind === 'created') return result;
    if (result.kind === 'unknown') return result;
    last = result;
  }
  return last;
}
